import { useState, useEffect } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { removeBackground, loadImage } from "@/lib/background-removal";
import { Users } from "lucide-react";

interface ConsultantAvatarProps {
  imageSrc: string;
  name: string;
  className?: string;
}

export const ConsultantAvatar = ({ imageSrc, name, className }: ConsultantAvatarProps) => {
  const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processImage = async () => {
      try {
        setIsProcessing(true);
        const img = await loadImage(imageSrc);
        const blob = await removeBackground(img);
        const url = URL.createObjectURL(blob);
        setProcessedImageUrl(url);
      } catch (error) {
        console.error('Failed to process image:', error);
        // Fallback to original image
        setProcessedImageUrl(imageSrc);
      } finally {
        setIsProcessing(false);
      }
    };

    processImage();

    return () => {
      if (processedImageUrl) {
        URL.revokeObjectURL(processedImageUrl);
      }
    };
  }, [imageSrc]);

  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();

  return (
    <Avatar className={className}>
      {isProcessing ? (
        <AvatarFallback className="bg-gradient-primary">
          <Users className="h-16 w-16 text-white" />
        </AvatarFallback>
      ) : (
        <>
          <AvatarImage src={processedImageUrl || imageSrc} alt={`${name} - Professional Business Consultant`} loading="lazy" />
          <AvatarFallback className="bg-gradient-primary text-white text-2xl">
            {initials}
          </AvatarFallback>
        </>
      )}
    </Avatar>
  );
};
